import { createSelector } from '@reduxjs/toolkit';
import { get, set } from 'lodash';
import { RootState } from '~/store';
import { oneBoxV3Api } from '~/api/oneBoxV3';
import { TOneBoxV3GetResourcesRecommendPayload } from '~/api/oneBoxV3/oneBoxV3.type';
import { TDownloadableResource } from '~/types/common.type';
import { createProgressInfo } from '~/utils/helper/downloadFile';
import {
  getterDownloadList,
  getterProgressList,
} from '../resource/resourceGetter';
import { RecommendMethod } from '~/components/AiPopup/AiDialog';

export const getterRecommend = createSelector(
  [
    (state: RootState) => state.aiAssistant.resources,
    (state, params: TOneBoxV3GetResourcesRecommendPayload) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      oneBoxV3Api.endpoints.getResourcesRecommend.select(params)(state)?.data,
  ],
  (initialData, resourceData) => {
    if (resourceData && resourceData.data && resourceData.data.data.length > 0)
      return resourceData.data.data;
    return initialData;
  },
);

export const getterMultiLevelRecommend = createSelector(
  [getterRecommend, getterProgressList, getterDownloadList],
  (resources, progressList, downloadList) => {
    const target: {
      [resource1: string]: {
        [resource2: string]: {
          [resource3: string]: TDownloadableResource[];
        };
      };
    } = {};

    resources.forEach(resource => {
      const progressItem = progressList.find(
        item => item.url === resource.file.url,
      );
      const downloadItem = downloadList.find(
        item => item.url === resource.file.url,
      );

      resource.resources1.forEach(resource1 => {
        const resources2 = resource.resources2.length
          ? resource.resources2
          : [resource1];
        resources2.forEach(resource2 => {
          const resources3 = resource.resources3.length
            ? resource.resources3
            : [resource2];
          resources3.forEach(resource3 => {
            const info = {
              resource,
              progress: createProgressInfo({ downloadItem, progressItem }),
            };

            set(
              target,
              [resource1, resource2, resource3],
              [...get(target, [resource1, resource2, resource3], []), info],
            );
          });
        });
      });
    });
    return target;
  },
);

export const getterSingleLevelRecommend = createSelector(
  [getterRecommend, getterProgressList, getterDownloadList],
  (resources, progressList, downloadList) => {
    return resources.map(resource => {
      const progressItem = progressList.find(
        item => item.url === resource.file.url,
      );
      const downloadItem = downloadList.find(
        item => item.url === resource.file.url,
      );
      return {
        resource,
        progress: createProgressInfo({ progressItem, downloadItem }),
      };
    });
  },
);

// const defaultRecommendSettings = {
//   // _id: '',
//   // userId: '',
//   resourceConfigs: {
//     // _id: '',
//     eduCode: '國小',
//     subject: '數學',
//     grade: '1',
//     // resources1: ['卷類資源'],
//   },
//   popUp: {
//     isFeatureEnabled: true,
//     reminderPeriod: 'always',
//     isDisplayPaused: false,
//     pauseStartDate: '',
//     pauseEndDate: '',
//   },
//   // weekAdjust: {
//   //   userYear: '',
//   //   userSemester: '',
//   //   userWeek: 1,
//   // },
//   // displayWeekInfos: {
//   //   currentYear: '',
//   //   currentSemester: '',
//   //   currentWeek: 1,
//   //   weekStart: null,
//   //   weekEnd: null,
//   //   lastWeek: 20,
//   //   userDisplayWeek: 8,
//   // },
// };

const defaultRecommendResourcesPayload = {
  year: '',
  weekNumber: 0,
  eduCode: '',
  subject: '',
  grade: '',
  resources1: [],
  pagination: {
    page: 1,
    pageSize: null,
    sort: null,
    paginateBy: 'resources2',
  },
};

// export const getterRecommendSetting = createSelector(
//   [(state: RootState) => state.aiAssistant.recommendSetting],
//   recommendSettings => {
//     if (!recommendSettings.popUp) {
//       return { ...recommendSettings, popUp: defaultRecommendSettings.popUp };
//     }
//     return recommendSettings;
//   },
// );

export const getterRecommendResourcesPayload = createSelector(
  [
    (state: RootState) => state.aiAssistant.recommendSetting,
    (state: RootState) => state.aiAssistant.selectedIndex,
    (state: RootState) => state.aiAssistant.recommendMethod,
  ],
  (recommendSetting, selectedIndex, recommendMethod) => {
    if (
      recommendSetting.resourceConfigs === null ||
      (recommendSetting.resourceConfigs &&
        recommendSetting.resourceConfigs.length === 0)
    )
      return defaultRecommendResourcesPayload;

    const { resourceConfigs, displayWeekInfos } = recommendSetting;
    const { eduCode, subject, grade, resources1 } =
      resourceConfigs[selectedIndex];
    const { currentYear, currentSemester, currentWeek, userDisplayWeek } =
      displayWeekInfos;
    const schoolYear =
      currentSemester === '1' ? `${currentYear}上` : `${currentYear}下`;
    const pagination = {
      page: 1,
      pageSize: null,
      sort: null,
      paginateBy: 'resources2',
    };
    return {
      year: schoolYear,
      weekNumber:
        recommendMethod === RecommendMethod.CUSTOMIZED
          ? userDisplayWeek
          : currentWeek,
      eduCode,
      subject,
      grade,
      resources1,
      pagination,
    };
  },
);
